import React, { useState, useRef, useEffect } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import InlineCamera from './InlineCamera';
import VerificationLog from './VerificationLog';
import VerificationResults from './VerificationResults';
import { fetchLocationData } from './ipinfoService';
import { ACTIVE_BRAND } from './brandingConfig';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import './InlineCamera.css';

// Disable all console logs in production only
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.error = () => {};
  console.warn = () => {};
  console.info = () => {};
  console.debug = () => {};
}

// Configure Amplify with Cognito Identity Pool
Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: 'us-east-1:09d5d355-cb05-4776-a1bb-dd6c06acb229',
      region: 'us-east-1',
      allowGuestAccess: true
    }
  }
});

// Your API endpoints
const API_CREATE_SESSION = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/CreateLivenessSession';
const API_GET_RESULTS = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/GetLivenessResults';
const API_EXTRACT_DOCUMENT = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/ExtractDocumentData';
const API_GET_UPLOAD_URL = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/GetUploadUrl';
const API_COMPARE_FACES = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/FaceCompare';
const API_DETECT_SIGNATURE = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/SignatureDetection';

function App() {
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [livenessResults, setLivenessResults] = useState(null);
  const [faceCompareResults, setFaceCompareResults] = useState(null);
  const [signatureResults, setSignatureResults] = useState(null);
  const [error, setError] = useState(null);
  
  // Document capture states
  const [currentStep, setCurrentStep] = useState('start');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionPromise, setExtractionPromise] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraSide, setCameraSide] = useState(null);
  
  // Session tracking states
  const [sessionLog, setSessionLog] = useState([]);
  const [linkId] = useState(() => Math.floor(Math.random() * 9000) + 1000);

  // Apply branding colors dynamically
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty('--brand-primary', ACTIVE_BRAND.colors.primary);
    root.style.setProperty('--brand-secondary', ACTIVE_BRAND.colors.secondary);
    root.style.setProperty('--brand-primary-hover', ACTIVE_BRAND.colors.primaryHover);
    root.style.setProperty('--brand-secondary-hover', ACTIVE_BRAND.colors.secondaryHover);
    
    // Update page title and theme color
    document.title = `${ACTIVE_BRAND.displayName} - Identity Verification`;
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', ACTIVE_BRAND.colors.primary);
    }
  }, []);

  // Capture initial page load session
  useEffect(() => {
    const captureInitialSession = async () => {
      const sessionData = await fetchLocationData();
      setSessionLog([sessionData]);
    };
    
    captureInitialSession();
  }, []);

  // Reset flow
  const resetFlow = () => {
    setCurrentStep('start');
    setFrontImage(null);
    setBackImage(null);
    setExtractedData(null);
    setLivenessResults(null);
    setFaceCompareResults(null);
    setSignatureResults(null);
    setError(null);
    setSessionId(null);
    setExtracting(false);
    setExtractionPromise(null);
    // Don't reset sessionLog - keep the history
  };

  // Open inline camera
  const openCamera = (side) => {
    setCameraSide(side);
    setShowCamera(true);
  };

  // Handle camera capture
  const handleCameraCapture = (base64String) => {
    if (cameraSide === 'front') {
      setFrontImage(base64String);
    } else {
      setBackImage(base64String);
    }
    setShowCamera(false);
    setCameraSide(null);
  };

  // Close camera
  const closeCamera = () => {
    setShowCamera(false);
    setCameraSide(null);
  };

  // Upload image to S3 and return S3 key
  const uploadImageToS3 = async (base64Image, side) => {
    try {
      // Get presigned URL
      const urlResponse = await fetch(API_GET_UPLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ side })
      });
      
      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }
      
      const { uploadUrl, s3Key, bucket } = await urlResponse.json();
      
      // Convert base64 to blob
      const byteCharacters = atob(base64Image);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });
      
      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/jpeg' }
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
      }
      
      return { s3Key, bucket };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  // Start document extraction using S3
  const startDocumentExtraction = async (frontImg, backImg = null) => {
    setExtracting(true);
    
    const extractPromise = (async () => {
      try {
        // Upload front image to S3
        const { s3Key: frontS3Key, bucket } = await uploadImageToS3(frontImg, 'front');
        
        // Upload back image to S3 if provided
        let backS3Key = null;
        if (backImg) {
          const backResult = await uploadImageToS3(backImg, 'back');
          backS3Key = backResult.s3Key;
        }
        
        // Call extraction Lambda with S3 keys
        const response = await fetch(API_EXTRACT_DOCUMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            frontS3Key,
            backS3Key,
            bucket
          })
        });
        
        if (!response.ok) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        
        const data = await response.json();
        
        if (data.success) {
          return data.extractedData;
        } else {
          throw new Error(data.error || 'Failed to extract document data');
        }
      } catch (error) {
        throw error;
      }
    })();

    setExtractionPromise(extractPromise);
    return extractPromise;
  };

  // Check camera availability
  const checkCameraAvailability = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      // Stop the stream immediately
      stream.getTracks().forEach(track => track.stop());
      return true;
    } catch (err) {
      console.warn('Camera check failed:', err);
      return false;
    }
  };

  // Proceed to liveness
  const proceedToLiveness = async () => {
    if (!frontImage) {
      setError('Please capture front of document');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Check camera availability first
      const cameraAvailable = await checkCameraAvailability();
      
      if (!cameraAvailable) {
        console.log('Camera not available - skipping liveness automatically');
        setLoading(false);
        // Set liveness as skipped with reason
        setLivenessResults({
          status: 'SUCCEEDED',
          confidence: 100,
          skipped: true,
          skipReason: 'Camera permission denied or camera not available',
          referenceImage: null
        });
        await skipLiveness();
        return;
      }

      // Start document extraction in background
      const extractionPromise = startDocumentExtraction(frontImage, backImage);

      // Start signature detection in background (after images uploaded to S3)
      const signaturePromise = (async () => {
        try {
          // Upload images to S3 first
          const { s3Key: frontS3Key, bucket } = await uploadImageToS3(frontImage, 'front-sig');
          let backS3Key = null;
          if (backImage) {
            const backResult = await uploadImageToS3(backImage, 'back-sig');
            backS3Key = backResult.s3Key;
          }
          
          // Call signature detection
          console.log('Starting background signature detection...');
          const signatureResponse = await fetch(API_DETECT_SIGNATURE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ frontS3Key, backS3Key, bucket }),
          });
          
          return await signatureResponse.json();
        } catch (err) {
          console.warn('Background signature detection failed:', err);
          return null;
        }
      })();

      // Store promises for later
      setExtractionPromise(extractionPromise);
      window.signaturePromise = signaturePromise; // Store globally

      // Create liveness session
      const response = await fetch(API_CREATE_SESSION);
      const data = await response.json();
      
      if (data.sessionId) {
        setSessionId(data.sessionId);
        setCurrentStep('liveness');
      } else {
        setError('Failed to create liveness session');
      }
    } catch (err) {
      setError('Error starting verification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Skip liveness and proceed to results
  const skipLiveness = async () => {
    if (!frontImage) {
      setError('Please capture front of document');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Start document extraction in background
      const extractionPromise = startDocumentExtraction(frontImage, backImage);

      // Start signature detection in background (after images uploaded to S3)
      const signaturePromise = (async () => {
        try {
          // Upload images to S3 first
          const { s3Key: frontS3Key, bucket } = await uploadImageToS3(frontImage, 'front-sig');
          let backS3Key = null;
          if (backImage) {
            const backResult = await uploadImageToS3(backImage, 'back-sig');
            backS3Key = backResult.s3Key;
          }
          
          // Call signature detection
          console.log('Starting background signature detection...');
          const signatureResponse = await fetch(API_DETECT_SIGNATURE, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ frontS3Key, backS3Key, bucket }),
          });
          
          return await signatureResponse.json();
        } catch (err) {
          console.warn('Background signature detection failed:', err);
          return null;
        }
      })();

      // Set mock liveness results with SUCCEEDED status (skipped)
      setLivenessResults({
        status: 'SUCCEEDED',
        confidence: 100,
        skipped: true,
        referenceImage: null
      });

      // Go to results page immediately - data will load progressively
      setCurrentStep('results');
      setLoading(false);

      // Portrait extraction promise
      const portraitPromise = (async () => {
        try {
          console.log('Extracting portrait from ID...');
          
          // Upload front and back images to S3
          const { s3Key: frontS3Key, bucket } = await uploadImageToS3(frontImage, 'front-compare-skip');
          
          let backS3Key = null;
          if (backImage) {
            const backResult = await uploadImageToS3(backImage, 'back-compare-skip');
            backS3Key = backResult.s3Key;
          }
          
          // Call FaceCompare - the Lambda will extract the portrait from the ID
          const faceCompareResponse = await fetch(API_COMPARE_FACES, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              selfieS3Key: frontS3Key, // Use front image as placeholder
              frontS3Key,
              backS3Key,
              bucket
            }),
          });
          
          const faceCompareData = await faceCompareResponse.json();
          return faceCompareData;
        } catch (err) {
          console.error('Portrait extraction error:', err);
          return null;
        }
      })();

      // Process results as they complete
      Promise.all([
        // Handle document extraction
        extractionPromise ? extractionPromise.then(data => {
          setExtractedData(data);
          setExtracting(false);
        }).catch(err => {
          console.error('Document extraction error:', err);
          setExtractedData({ error: 'Document extraction failed: ' + err.message });
          setExtracting(false);
        }) : Promise.resolve(),
        
        // Handle portrait extraction
        portraitPromise.then(faceCompareData => {
          // Override with skipped status but keep portrait
          setFaceCompareResults({
            matched: true,
            similarity: 100,
            skipped: true,
            frontMatch: { similarity: 100 },
            backMatch: { similarity: 0 },
            portraitImage: faceCompareData?.portraitImage || null
          });
        }),
        
        // Handle signature detection
        signaturePromise.then(data => {
          setSignatureResults(data);
        }).catch(err => {
          console.warn('Signature detection error:', err);
          setSignatureResults(null);
        })
      ]).then(() => {
        // All done - capture completion session
        fetchLocationData().then(completionSession => {
          completionSession.completed = true;
          setSessionLog(prev => {
            // Avoid duplicates - only add if not already completed
            const hasCompleted = prev.some(s => s.completed);
            return hasCompleted ? prev : [...prev, completionSession];
          });
        });
      });
    } catch (err) {
      setError('Error processing verification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful liveness check
  const handleAnalysisComplete = async () => {
    setLoading(true);
    
    try {
      // Get liveness results
      const livenessResponse = await fetch(API_GET_RESULTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });
      
      const livenessData = await livenessResponse.json();
      setLivenessResults(livenessData);
      setSessionId(null);

      // Go to results page immediately - data will load progressively
      setCurrentStep('results');
      setLoading(false);

      // Start face comparison immediately (don't wait for OCR)
      const faceComparePromise = (async () => {
        if (livenessData.referenceImage && frontImage) {
          console.log('Starting face comparison using S3...');
          try {
            // Upload selfie to S3
            const { s3Key: selfieS3Key, bucket } = await uploadImageToS3(livenessData.referenceImage, 'selfie');
            
            // Upload front and back images to S3
            const { s3Key: frontS3Key } = await uploadImageToS3(frontImage, 'front-compare');
            
            console.log(`Selfie uploaded to: s3://${bucket}/${selfieS3Key}`);
            console.log(`Front image at: s3://${bucket}/${frontS3Key}`);
            
            // Upload back image to S3 if available
            let backS3Key = null;
            if (backImage) {
              const backResult = await uploadImageToS3(backImage, 'back-compare');
              backS3Key = backResult.s3Key;
              console.log(`Back image uploaded to: s3://${bucket}/${backS3Key}`);
            }
            
            // Face comparison
            const faceCompareResponse = await fetch(API_COMPARE_FACES, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                selfieS3Key,
                frontS3Key,
                backS3Key,
                bucket
              }),
            });
            
            const faceCompareData = await faceCompareResponse.json();
            console.log('Face comparison results:', faceCompareData);
            return faceCompareData;
          } catch (err) {
            console.error('Face comparison error:', err);
            return {
              matched: false,
              similarity: 0,
              error: 'Face comparison failed: ' + err.message
            };
          }
        }
        return null;
      })();

      // Process results as they complete
      Promise.all([
        // Handle document extraction
        extractionPromise ? extractionPromise.then(data => {
          setExtractedData(data);
          setExtracting(false);
        }).catch(err => {
          console.error('Document extraction error:', err);
          setExtractedData({ error: 'Document extraction failed: ' + err.message });
          setExtracting(false);
        }) : Promise.resolve(),
        
        // Handle face comparison
        faceComparePromise.then(data => {
          if (data) setFaceCompareResults(data);
        }),
        
        // Handle signature detection
        window.signaturePromise ? window.signaturePromise.then(data => {
          setSignatureResults(data);
          delete window.signaturePromise;
        }).catch(err => {
          console.warn('Signature detection error:', err);
          setSignatureResults(null);
          delete window.signaturePromise;
        }) : Promise.resolve()
      ]).then(() => {
        // All done - capture completion session
        fetchLocationData().then(completionSession => {
          completionSession.completed = true;
          setSessionLog(prev => {
            // Avoid duplicates - only add if not already completed
            const hasCompleted = prev.some(s => s.completed);
            return hasCompleted ? prev : [...prev, completionSession];
          });
        });
      });

    } catch (err) {
      setError('Error getting results: ' + err.message);
      setLoading(false);
    }
  };

  // Handle errors
  const handleError = (error) => {
    console.error('Liveness check error:', error);
    
    // Check if it's a camera permission or availability error
    const errorMessage = error.message?.toLowerCase() || '';
    const isCameraError = errorMessage.includes('camera') || 
                         errorMessage.includes('permission') || 
                         errorMessage.includes('notfound') ||
                         errorMessage.includes('notallowed') ||
                         errorMessage.includes('denied');
    
    if (isCameraError) {
      console.log('Camera not available or permission denied - skipping liveness');
      // Automatically skip liveness
      setSessionId(null);
      skipLiveness();
    } else {
      setError('Liveness check failed: ' + error.message);
      setSessionId(null);
      setCurrentStep('document');
    }
  };

  // Start verification flow
  const startVerification = () => {
    setCurrentStep('document');
  };

  return (
    <ThemeProvider>
      <div className="App">
      <header className={`App-header ${ACTIVE_BRAND.name === 'NEXTPAYMENT' ? 'nextpayment-header' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-header' : ''}`}>
        <img src={ACTIVE_BRAND.logo} alt={`${ACTIVE_BRAND.displayName} Logo`} className="logo" />
        <h1>Identity Verification</h1>
        <p>Secure face liveness detection and document verification powered by IDV Pacific</p>
      </header>        <div className="container">
          {/* Progress Bar */}
          {currentStep !== 'start' && (
            <div className="progress-bar">
              <div className={`progress-step ${currentStep === 'document' ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <div className="step-label">Document</div>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${currentStep === 'liveness' ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <div className="step-label">Face Verification</div>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${currentStep === 'results' ? 'active' : ''}`}>
                <div className="step-number">3</div>
                <div className="step-label">Results</div>
              </div>
            </div>
          )}

          {/* Start Screen */}
          {currentStep === 'start' && (
            <div className="start-section">
              <div className="info-card">
                <h2>Welcome to IDV Pacific</h2>
                <p>Complete your identity verification in 3 simple steps:</p>
                <ul className="features-list">
                  <li>📸 Capture your ID document (front and back)</li>
                  <li>👤 Complete face liveness check (optional)</li>
                  <li>✓ Receive instant verification results</li>
                </ul>
              </div>
              <button 
                onClick={startVerification}
                className={`start-button ${ACTIVE_BRAND.name === 'BARE_MEDIA' ? 'bare-media-button' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-button' : ''}`}
              >
                Start Verification
              </button>
            </div>
          )}

          {/* Document Capture */}
          {currentStep === 'document' && (
            <div className="document-section">
              <div className="document-card">
                <h2>Step 1: Capture Your ID</h2>
                <p className="step-instructions">
                  Use your camera to capture both sides of your ID document
                </p>

                <div className="document-capture-grid">
                  {/* Front Side */}
                  <div className="capture-box">
                    <h3>Front of ID <span className="required">*</span></h3>
                    {!frontImage ? (
                      <div className="camera-guide-simple">
                        <div className="camera-icon-large">📷</div>
                        <p className="camera-hint">Tap to open camera</p>
                        <button 
                          className={`open-camera-btn ${ACTIVE_BRAND.name === 'BARE_MEDIA' ? 'bare-media-button' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-button' : ''}`}
                          onClick={() => openCamera('front')}
                        >
                          Open Camera
                        </button>
                      </div>
                    ) : (
                      <div className="image-preview">
                        <div className="preview-header">
                          <span className="preview-label">✓ Captured</span>
                        </div>
                        <img src={`data:image/jpeg;base64,${frontImage}`} alt="Front of ID" />
                        <div className="preview-actions">
                          <button 
                            className="retake-button"
                            onClick={() => {
                              setFrontImage(null);
                            }}
                          >
                            🔄 Retake
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Back Side */}
                  <div className="capture-box">
                    <h3>Back of ID <span className="optional">(Optional)</span></h3>
                    {!backImage ? (
                      <div className="camera-guide-simple">
                        <div className="camera-icon-large">📷</div>
                        <p className="camera-hint">Tap to open camera</p>
                        <button 
                          className={`open-camera-btn ${ACTIVE_BRAND.name === 'BARE_MEDIA' ? 'bare-media-button' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-button' : ''}`}
                          onClick={() => openCamera('back')}
                        >
                          Open Camera
                        </button>
                      </div>
                    ) : (
                      <div className="image-preview">
                        <div className="preview-header">
                          <span className="preview-label">✓ Captured</span>
                        </div>
                        <img src={`data:image/jpeg;base64,${backImage}`} alt="Back of ID" />
                        <div className="preview-actions">
                          <button 
                            className="retake-button"
                            onClick={() => {
                              setBackImage(null);
                            }}
                          >
                            🔄 Retake
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="action-buttons-group">
                  <button 
                    onClick={proceedToLiveness}
                    disabled={!frontImage || loading}
                    className={`action-button ${ACTIVE_BRAND.name === 'BARE_MEDIA' ? 'bare-media-button' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-button' : ''}`}
                  >
                    {loading ? 'Starting...' : 'Continue to Face Verification'}
                  </button>
                  <button 
                    onClick={skipLiveness}
                    disabled={!frontImage || loading}
                    className="action-button-secondary"
                  >
                    {loading ? 'Processing...' : 'Skip Face Verification'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Face Liveness */}
          {currentStep === 'liveness' && sessionId && (
            <div className="liveness-section">
              <h3 className="step-title">Step 2: Face Liveness Verification</h3>
              {extracting && (
                <div className="processing-indicator">
                  <div className="spinner-small"></div>
                  <p>Processing document in background...</p>
                </div>
              )}
              <FaceLivenessDetector
                sessionId={sessionId}
                region="us-east-1"
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleError}
                disableInstructionScreen={false}
              />
            </div>
          )}

          {/* Results */}
          {currentStep === 'results' && (
            <div className="results-section">
              <VerificationResults
                verificationId={linkId.toString()}
                livenessResults={livenessResults}
                documentData={extractedData}
                faceCompareResults={faceCompareResults}
                signatureResults={signatureResults}
                referenceImage={livenessResults?.referenceImage}
                frontImage={frontImage}
                backImage={backImage}
                onReset={resetFlow}
              />

              {/* Verification Log */}
              <VerificationLog sessions={sessionLog} linkId={linkId} />
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-section">
              <div className="error-card">
                <h3>Verification Error</h3>
                <p className="error-message">{error}</p>
                <button 
                  onClick={resetFlow}
                  className={`action-button ${ACTIVE_BRAND.name === 'BARE_MEDIA' ? 'bare-media-button' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-button' : ''}`}
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && !sessionId && (
            <div className="loading-section">
              <div className="spinner"></div>
              <p>Processing...</p>
            </div>
          )}
        </div>

        <footer className={`App-footer ${ACTIVE_BRAND.name === 'NEXTPAYMENT' ? 'nextpayment-footer' : ''} ${ACTIVE_BRAND.name === 'AAA' ? 'aaa-footer' : ''}`}>
          <p>Identity verification powered by <strong>IDV Pacific</strong></p>
          <p className="footer-links">
            <a href="https://www.idvpacific.com.au/contact/" target="_blank" rel="noopener noreferrer">Contact Us</a>
            {' | '}
            <a href="https://www.idvpacific.com.au" target="_blank" rel="noopener noreferrer">About IDV Pacific</a>
          </p>
        </footer>
      </div>

      {/* Camera Overlay */}
      {showCamera && (
        <InlineCamera
          side={cameraSide}
          onCapture={handleCameraCapture}
          onClose={closeCamera}
        />
      )}
    </ThemeProvider>
  );
}

function formatFieldName(key) {
  // Special case for backAddress
  if (key === 'backAddress') {
    return 'Change of address label';
  }
  
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, str => str.toUpperCase())
    .trim();
}

export default App;